// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback } from 'react';
import {
  Pressable,
  Modal as RNModal,
  StyleSheet,
  Text,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { createTheme } from '../../design-system/theme';
import { useColorScheme } from '../../foundations/colorScheme';
import { callHistoryStrings } from '../strings';
import type { CallHistoryActions, CallHistoryEntry } from '../types';

export interface CallHistoryOptionMenuProps {
  readonly visible: boolean;
  readonly entry: CallHistoryEntry | null;
  readonly actions?: CallHistoryActions;
  readonly onClose: () => void;
}

interface MenuItem {
  readonly key: string;
  readonly label: string;
  readonly onPress: () => void;
  readonly destructive?: boolean;
}

export const CallHistoryOptionMenu: React.FC<CallHistoryOptionMenuProps> = ({
  visible,
  entry,
  actions,
  onClose,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme);

  const handleAction = useCallback(
    (run: () => void) => () => {
      run();
      onClose();
    },
    [onClose],
  );

  if (entry == null) {
    return null;
  }

  const items: readonly MenuItem[] = buildMenuItems(entry, actions, handleAction);

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      accessibilityViewIsModal
    >
      <Pressable
        style={[styles.overlay, { backgroundColor: theme.colors.overlay.scrim }]}
        onPress={onClose}
      >
        <Pressable
          onPress={(event) => event.stopPropagation()}
          style={[
            styles.shell,
            {
              backgroundColor: theme.colors.surface.primary,
              borderColor: theme.colors.border.secondary,
            },
          ]}
          accessibilityRole="menu"
        >
          {items.map((item) => (
            <Pressable
              key={item.key}
              onPress={item.onPress}
              accessibilityRole="menuitem"
              accessibilityLabel={item.label}
              style={({ pressed }) => [
                styles.item,
                {
                  backgroundColor: pressed ? theme.colors.surface.secondary : 'transparent',
                },
              ]}
            >
              <Text
                style={[
                  styles.itemText,
                  {
                    color: item.destructive ? theme.colors.accent.rose : theme.colors.text.primary,
                  },
                ]}
              >
                {item.label}
              </Text>
            </Pressable>
          ))}
        </Pressable>
      </Pressable>
    </RNModal>
  );
};

function buildMenuItems(
  entry: CallHistoryEntry,
  actions: CallHistoryActions | undefined,
  wrap: (run: () => void) => () => void,
): readonly MenuItem[] {
  if (actions == null) {
    return [];
  }
  const out: MenuItem[] = [];
  if (actions.onCallBack) {
    out.push({
      key: 'call-back',
      label: callHistoryStrings.actionCallBack,
      onPress: wrap(() => actions.onCallBack?.(entry)),
    });
  }
  if (actions.onMessage) {
    out.push({
      key: 'message',
      label: callHistoryStrings.actionMessage,
      onPress: wrap(() => actions.onMessage?.(entry)),
    });
  }
  if (actions.onViewProfile) {
    out.push({
      key: 'view-profile',
      label: callHistoryStrings.actionViewProfile,
      onPress: wrap(() => actions.onViewProfile?.(entry)),
    });
  }
  if (actions.onToggleFavorite) {
    out.push({
      key: 'favorite',
      label: entry.summary.isFavorite
        ? callHistoryStrings.actionRemoveFromFavorites
        : callHistoryStrings.actionAddToFavorites,
      onPress: wrap(() => {
        void actions.onToggleFavorite?.(entry.summary.id, !entry.summary.isFavorite);
      }),
    });
  }
  if (actions.onDelete) {
    out.push({
      key: 'delete',
      label: callHistoryStrings.actionDelete,
      onPress: wrap(() => {
        void actions.onDelete?.(entry.summary.id);
      }),
      destructive: true,
    });
  }
  return out;
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  } satisfies ViewStyle,
  shell: {
    width: '100%',
    maxWidth: 280,
    borderRadius: 14,
    borderWidth: 1,
    overflow: 'hidden',
    paddingVertical: 4,
  } satisfies ViewStyle,
  item: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  } satisfies ViewStyle,
  itemText: {
    fontSize: 14,
    fontWeight: '500',
  } satisfies TextStyle,
});
